import { z } from 'zod';

import { apiClient } from '@/lib/api-client';
import {
  auditEntrySchema,
  hallOfFameEntrySchema,
  mapResponseSchema,
  blockedUserSchema,
  notificationPageSchema,
  reportSchema,
  searchResultsSchema,
  tagResultSchema,
  townPulseSchema,
  type ReportReason,
} from '@/lib/schemas/discovery';

const acknowledgement = z.object({ message: z.string() });
const blockResult = z.object({ isBlocked: z.boolean() });

export const discoveryApi = {
  search: (query: string, scope: string, signal?: AbortSignal) =>
    apiClient.get('/search', searchResultsSchema, {
      query: { q: query, scope, limit: '20' },
      ...(signal ? { signal } : {}),
    }),

  trendingTags: (params: Record<string, string>, signal?: AbortSignal) =>
    apiClient.get('/tags/trending', z.array(tagResultSchema), {
      query: params,
      ...(signal ? { signal } : {}),
    }),

  map: (window: string, signal?: AbortSignal) =>
    apiClient.get('/stats/map', mapResponseSchema, {
      query: { window },
      ...(signal ? { signal } : {}),
    }),

  hallOfFame: (params: Record<string, string>, signal?: AbortSignal) =>
    apiClient.get('/ideals/hall-of-fame', z.array(hallOfFameEntrySchema), {
      query: params,
      ...(signal ? { signal } : {}),
    }),

  pulse: (params: Record<string, string>, signal?: AbortSignal) =>
    apiClient.get('/stats/pulse', townPulseSchema, {
      query: params,
      ...(signal ? { signal } : {}),
    }),

  // --- notifications ---
  notifications: (signal?: AbortSignal) =>
    apiClient.get('/users/me/notifications', notificationPageSchema, signal ? { signal } : {}),

  unreadCount: (signal?: AbortSignal) =>
    apiClient.get(
      '/users/me/notifications/count',
      z.object({ unread: z.number() }),
      signal ? { signal } : {},
    ),

  markRead: (notificationIds?: string[]) =>
    apiClient.post('/users/me/notifications/read', z.object({ marked: z.number() }), {
      body: notificationIds ? { notificationIds } : {},
    }),

  // --- safety ---
  reportPost: (postId: string, reason: ReportReason, details?: string) =>
    apiClient.post(`/posts/${postId}/report`, acknowledgement, {
      body: { reason, ...(details ? { details } : {}) },
    }),

  reportComment: (commentId: string, reason: ReportReason, details?: string) =>
    apiClient.post(`/comments/${commentId}/report`, acknowledgement, {
      body: { reason, ...(details ? { details } : {}) },
    }),

  block: (userId: string) => apiClient.post(`/users/${userId}/block`, blockResult),
  unblock: (userId: string) => apiClient.delete(`/users/${userId}/block`, blockResult),
  listBlocked: (signal?: AbortSignal) =>
    apiClient.get('/users/me/blocks', z.array(blockedUserSchema), signal ? { signal } : {}),

  // --- moderation ---
  reports: (status: string, signal?: AbortSignal) =>
    apiClient.get('/moderation/reports', z.array(reportSchema), {
      query: { status },
      ...(signal ? { signal } : {}),
    }),

  openReportCount: (signal?: AbortSignal) =>
    apiClient.get(
      '/moderation/reports/count',
      z.object({ open: z.number() }),
      signal ? { signal } : {},
    ),

  resolveReport: (id: string, status: 'ACTIONED' | 'DISMISSED') =>
    apiClient.patch(`/moderation/reports/${id}`, z.object({ status: z.string() }), {
      body: { status },
    }),

  hidePost: (id: string, reason?: string) =>
    apiClient.post(`/moderation/posts/${id}/hide`, z.null(), {
      body: reason ? { reason } : {},
    }),

  removePost: (id: string, reason?: string) =>
    apiClient.post(`/moderation/posts/${id}/remove`, z.null(), {
      body: reason ? { reason } : {},
    }),

  removeComment: (id: string, reason?: string) =>
    apiClient.post(`/moderation/comments/${id}/remove`, z.null(), {
      body: reason ? { reason } : {},
    }),

  suspendUser: (id: string, reason?: string) =>
    apiClient.post(`/moderation/users/${id}/suspend`, z.null(), {
      body: reason ? { reason } : {},
    }),

  auditLog: (signal?: AbortSignal) =>
    apiClient.get('/moderation/audit', z.array(auditEntrySchema), signal ? { signal } : {}),
};
